import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { FooterLinksSection, FooterLinksSectionBaseProps } from './FooterLinksSection';
import { FooterMobileStoresBaseProps } from './AdditionalFooterSections/FooterMobileStores';
import { useTheme } from '../../../../theming/useTheme';
import { VrLineHeight } from '../../../../theming/verticalRhythm/verticalRhythm';
import { SocialSectionDataProps } from './AdditionalFooterSections/FooterSocialLinks';
import { generateSeparatorSections } from './footer.utils';
import { AdditionalFooterSections } from './AdditionalFooterSections/AdditionalFooterSections';
import { ErrorBoundary } from '../../../errorHandling/ErrorBoundary';
import { BottomFooterBar, BottomFooterBarDataProps } from './BottomFooterBar';
import { MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';

const componentConfigurationKey = 'footer';

interface FooterDataProps {
  linksSection: FooterLinksSectionBaseProps | null,
  socialSection: SocialSectionDataProps | null,
  appsSection: FooterMobileStoresBaseProps | null,
  bottomFooterBar: BottomFooterBarDataProps
}

interface FooterComponentThemeProps {
  backgroundColor: string,
  borderTopColor: string,
  separatorColor: string,
  vrLineHeight: VrLineHeight,
}

type FooterComponentProps = FooterDataProps & FooterComponentThemeProps;

const getFooterStyle = (theme: FooterComponentThemeProps) => {
  const { vrLineHeight, borderTopColor, backgroundColor } = theme;
  return StyleSheet.create({
    footer: {
      backgroundColor,
      borderTop: `solid 2px ${borderTopColor}`,
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        padding: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        padding: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        padding: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        padding: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.75}px`,
      },
    },
    footerContent: {
      margin: 'auto',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        maxWidth: '1088px',
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        maxWidth: '852px',
      },
    },
  });
};

const getSeparatorStyle = (separatorColor: string) => {
  return StyleSheet.create({
    style: {
      height: '1px',
      backgroundColor: separatorColor,
    },
  });
};

const getLinksSection = (linksSection: FooterLinksSectionBaseProps | null) => {
  return linksSection ? <FooterLinksSection key="footer-links" {...linksSection as FooterLinksSectionBaseProps} /> : null;
};

const getAdditionalFooterSection = (socialSection: SocialSectionDataProps | null, appsSection: FooterMobileStoresBaseProps | null, separatorColor: string) => {
  if (socialSection === null && appsSection === null) return null;
  return <AdditionalFooterSections key="additional-links" socialSection={socialSection} appsSection={appsSection} separatorColor={separatorColor} />;
};

const getFooterItems = (linksSection: FooterLinksSectionBaseProps | null, socialSection: SocialSectionDataProps | null, appsSection: FooterMobileStoresBaseProps | null, separatorColor:string) => {
  return [
    getLinksSection(linksSection),
    getAdditionalFooterSection(socialSection, appsSection, separatorColor),
  ]
    .filter(section => section !== null)
    .reduce(generateSeparatorSections(getSeparatorStyle(separatorColor).style), []);
};

export const FooterComponent: React.FunctionComponent<FooterComponentProps> = props => {
  const footerComponentStyle = getFooterStyle(props);
  const { linksSection, socialSection, appsSection, separatorColor, bottomFooterBar } = props;
  return (
    <footer>
      <div className={css(footerComponentStyle.footer)}>
        <div className={css(footerComponentStyle.footerContent)}>
          {getFooterItems(linksSection, socialSection, appsSection, separatorColor)}
        </div>
      </div>
      <BottomFooterBar {...bottomFooterBar} />
    </footer>
  );
};

const mapThemeProps = (theme: FooterComponentThemeProps) => {
  return {
    backgroundColor: theme.backgroundColor,
    borderTopColor: theme.borderTopColor,
    separatorColor: theme.separatorColor,
    vrLineHeight: theme.vrLineHeight,
  };
};

export const Footer = (props: FooterDataProps) => {
  const themeContext = useTheme(componentConfigurationKey, mapThemeProps);
  return <ErrorBoundary><FooterComponent {...themeContext} {...props} /></ErrorBoundary>;
};
